import { useLocation } from 'react-router';
import SegmentAnalytics from 'ff/common/SegmentAnalytics';
import { getCurrentPage } from '../utils';

interface EventProperties {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

function getCurrentEnvironment(): string {
    const hostname = window.location.hostname;

    if (hostname === 'beta.foreflight.com') {
        return 'beta';
    } else if (hostname === 'plan.foreflight.com') {
        return 'production';
    }

    // Default to 'development' for local development or unknown hosts
    return 'development';
}

export function useAnalytics() {
    const location = useLocation();

    const logEvent = (eventName: string, properties?: EventProperties) => {
        const currentPage = getCurrentPage(location.pathname);
        const environment = getCurrentEnvironment();

        const enhancedProperties = {
            ...properties,
            currentPage,
            environment,
        };

        SegmentAnalytics.logEvent(eventName, enhancedProperties);
    };

    const logDebriefButtonClick = (properties: {
        score: number;
        iapCount: number;
    }) => {
        logEvent('Debrief, Debrief Button Clicked', {
            score: properties.score,
            iapCount: properties.iapCount,
        });
    };

    const logAxisDataSelected = (properties: {
        axis: 'left' | 'right';
        originalValue: string;
        newValue: string;
    }) => {
        logEvent('Debrief, Profile Axis Data Selected', {
            axis: properties.axis,
            originalValue: properties.originalValue,
            newValue: properties.newValue,
        });
    };

    const logSegmentClicked = (properties: {
        segmentName: string;
        segmentScore: number | null;
    }) => {
        logEvent('Debrief, Segment Clicked', {
            segmentName: properties.segmentName,
            segmentScore: properties.segmentScore,
        });
    };

    const logIAPChanged = (properties: {
        previousApproach: string;
        newApproach: string;
        isVisualApproach: boolean;
    }) => {
        logEvent('Debrief, IAP Changed', {
            previousApproach: properties.previousApproach,
            newApproach: properties.newApproach,
            isVisualApproach: properties.isVisualApproach,
        });
    };

    const logTrackLogDownloaded = (properties: { fileType: string }) => {
        logEvent('Debrief, Track Log Downloaded', {
            fileType: properties.fileType,
        });
    };

    const logShareButtonClicked = () => {
        logEvent('Debrief, Share Button Clicked');
    };

    const logInfoDialogOpened = (properties: { score: number | null }) => {
        logEvent('Debrief, Info Dialog Opened', {
            score: properties.score,
        });
    };

    const logMakePrivateClicked = () => {
        logEvent('Debrief, Make Private Clicked');
    };

    const logTrackLogDeleted = () => {
        logEvent('Debrief, Track Log Deleted');
    };

    const logTrackLogSelected = (properties: {
        score: number | null;
        shared: boolean;
    }) => {
        logEvent('Debrief, Track Log Selected', {
            score: properties.score,
            shared: properties.shared,
        });
    };

    const logTailNumberChanged = (properties: {
        previousTail: string;
        newTail: string | null;
        newManufacturer: string | null;
        newModel: string | null;
    }) => {
        logEvent('Debrief, Tail Number Changed', {
            previousTail: properties.previousTail,
            newTail: properties.newTail,
            newManufacturer: properties.newManufacturer,
            newModel: properties.newModel,
        });
    };

    const logAircraftModelChanged = (properties: {
        previousManufacturer: string | null;
        previousModel: string | null;
        newManufacturer: string | null;
        newModel: string | null;
    }) => {
        logEvent('Debrief, Aircraft Model Changed', {
            previousManufacturer: properties.previousManufacturer,
            previousModel: properties.previousModel,
            newManufacturer: properties.newManufacturer,
            newModel: properties.newModel,
        });
    };

    const logVSpeedsSaved = (properties: {
        manufacturer: string | null;
        model: string | null;
        isCustom: boolean;
        hasStandardSpeeds: boolean;
        hasExistingOverrides: boolean;
        vSpeedCount: number;
        changedVSpeedCount: number;
        changedVSpeeds: string[];
    }) => {
        logEvent('Debrief, V-Speeds Saved', {
            manufacturer: properties.manufacturer,
            model: properties.model,
            isCustom: properties.isCustom,
        });
    };

    const logDeclutterButtonClicked = (properties: {
        state: 'open' | 'closed';
    }) => {
        logEvent('Debrief, Declutter Button Clicked', {
            state: properties.state,
        });
    };

    const logVRefOverridden = (properties: {
        approachType: string;
        originalValue: number;
        overrideValue: number;
    }) => {
        logEvent('Debrief, VRef Overridden', {
            approachType: properties.approachType,
            originalValue: properties.originalValue,
            overrideValue: properties.overrideValue,
        });
    };

    const logTimelineSpeedChanged = (properties: {
        speed: number;
        whilePlaying: boolean;
    }) => {
        logEvent('Debrief, Timeline Speed Changed', {
            speed: properties.speed,
            whilePlaying: properties.whilePlaying,
        });
    };

    const logMapLayerChanged = (properties: { layerName: string }) => {
        logEvent('Debrief, Map Layer Changed', {
            layerName: properties.layerName,
        });
    };

    const logInstrumentsToggled = (properties: {
        state: 'open' | 'closed';
    }) => {
        logEvent('Debrief, Instruments Toggled', {
            state: properties.state,
        });
    };

    const logDataPaneToggled = (properties: { state: 'open' | 'closed' }) => {
        logEvent('Debrief, Data Pane Toggled', {
            state: properties.state,
        });
    };

    const logScoreDetailsCollapseToggled = (properties: {
        state: 'open' | 'closed';
    }) => {
        logEvent('Debrief, Score Details Collapse Toggled', {
            state: properties.state,
        });
    };

    const logWhatsNewItemViewed = (properties: {
        itemName: string;
        trigger: 'dropdown' | 'next_button';
        wasUnread: boolean;
    }) => {
        logEvent('Debrief, Whats New Item Viewed', {
            itemName: properties.itemName,
            trigger: properties.trigger,
            wasUnread: properties.wasUnread,
        });
    };

    const logNonScoredModeToggled = (properties: {
        toggledState: 'On' | 'Off';
    }) => {
        logEvent('Debrief, Scoring Mode Toggled', {
            toggledState: properties.toggledState,
        });
    };

    return {
        logEvent: logEvent,
        logDebriefButtonClick: logDebriefButtonClick,
        logAxisDataSelected: logAxisDataSelected,
        logSegmentClicked: logSegmentClicked,
        logIAPChanged: logIAPChanged,
        logTrackLogDownloaded: logTrackLogDownloaded,
        logShareButtonClicked: logShareButtonClicked,
        logInfoDialogOpened: logInfoDialogOpened,
        logMakePrivateClicked: logMakePrivateClicked,
        logTrackLogDeleted: logTrackLogDeleted,
        logTrackLogSelected: logTrackLogSelected,
        logTailNumberChanged: logTailNumberChanged,
        logAircraftModelChanged: logAircraftModelChanged,
        logVSpeedsSaved: logVSpeedsSaved,
        logDeclutterButtonClicked: logDeclutterButtonClicked,
        logVRefOverridden: logVRefOverridden,
        logTimelineSpeedChanged: logTimelineSpeedChanged,
        logMapLayerChanged: logMapLayerChanged,
        logInstrumentsToggled: logInstrumentsToggled,
        logDataPaneToggled: logDataPaneToggled,
        logScoreDetailsCollapseToggled: logScoreDetailsCollapseToggled,
        logWhatsNewItemViewed: logWhatsNewItemViewed,
        logNonScoredModeToggled: logNonScoredModeToggled,
    };
}
